import { OpenFaireDirectFirstOrderIncentiveModalButton } from "@faire/retailer-visitor-shared/components/Notifications/Components/FaireDirectIncentivesBanner/FaireDirectFirstOrderIncentiveBanner/__internal__/OpenFaireDirectFirstOrderIncentiveModalButton";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";

const FaireDirectFirstOrderIncentiveBannerBase: React.FC = () => {
  const {
    brandName,
    isLastDayBeforeExpiry,
    isSecondLastDayBeforeExpiry,
    isFaireDirectCreditPercentageBased,
    formattedFixedBasedAmount,
    formattedPercentageBasedNumber,
  } = useFaireDirectFirstOrderIncentive();

  if (isFaireDirectCreditPercentageBased) {
    if (isLastDayBeforeExpiry) {
      return (
        <BannerWrapper>
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is less than 24 hours (a day).",
              img: "https://cdn.faire.com/fastly/a33af7ae726a495f055999255118d5c238d000ef597ac303d2e3af485d6d2ae4.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              IncentiveModalOpenLinkFormatting:
                OpenFaireDirectFirstOrderIncentiveModalButton,
              percentageBasedNumber: formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </BannerWrapper>
      );
    } else if (isSecondLastDayBeforeExpiry) {
      return (
        <BannerWrapper>
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is less than 2 days.",
              img: "https://cdn.faire.com/fastly/cf58be7e89b13a0be4c1e6a313f0d5edd73f94a0f9cf18a21bb685b34aa16da1.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              IncentiveModalOpenLinkFormatting:
                OpenFaireDirectFirstOrderIncentiveModalButton,
              percentageBasedNumber: formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </BannerWrapper>
      );
    } else {
      return (
        <BannerWrapper>
          <StrictLocalMsg
            defaultMessage="Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is greater than 2 days.",
              img: "https://cdn.faire.com/fastly/f392415027ca1ebb6d7798cc86cc5cb72fe04ac828c1d50fe62ed8d2e1ceca30.png",
            }}
            values={{
              IncentiveModalOpenLinkFormatting:
                OpenFaireDirectFirstOrderIncentiveModalButton,
              percentageBasedNumber: formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </BannerWrapper>
      );
    }
  }

  if (isLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is less than 24 hours (a day).",
            img: "https://cdn.faire.com/fastly/a33af7ae726a495f055999255118d5c238d000ef597ac303d2e3af485d6d2ae4.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName ?? "",
          }}
        />
      </BannerWrapper>
    );
  } else if (isSecondLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is less than 2 days.",
            img: "https://cdn.faire.com/fastly/cf58be7e89b13a0be4c1e6a313f0d5edd73f94a0f9cf18a21bb685b34aa16da1.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName ?? "",
          }}
        />
      </BannerWrapper>
    );
  } else {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          defaultMessage="Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is greater than 2 days.",
            img: "https://cdn.faire.com/fastly/f392415027ca1ebb6d7798cc86cc5cb72fe04ac828c1d50fe62ed8d2e1ceca30.png",
          }}
          values={{
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName ?? "",
          }}
        />
      </BannerWrapper>
    );
  }
};

export const FaireDirectFirstOrderIncentiveBanner = observer(
  FaireDirectFirstOrderIncentiveBannerBase
);

const BannerWrapper = ({ children }: { children: React.ReactNode }) => (
  <Typography
    align="center"
    data-test-id="faire-direct-first-order-incentive-banner"
  >
    {children}
  </Typography>
);
